// src/components/Navigation.js
import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useCart } from "../../context/CartContext.js";
import {
  Image,
  ShoppingCart,
  Menu,
  X,
  Upload as UploadIcon,
  Shield,
  Receipt,
  Crosshair,
  RefreshCcw,
  Info,
  Home,
  Tag,
  QrCode,
  FileSignature,
  Map,
} from "lucide-react";
import QRModal from "../Common/QRModal";
import "../../styles/Navigation.css";
import "../../styles/QRModal.css";

function Navigation() {
  const location = useLocation();
  const { items, catalogItems, clearCart, clearCatalogCart } = useCart();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  // Admin flag & helper
  const isAdmin =
    new URLSearchParams(location.search).get("admin")?.toLowerCase() === "true";

  // Ensure ?admin=true stays on every internal link
  const makePath = (path) => {
    if (!isAdmin) return path;
    const hasQuery = path.includes("?");
    return `${path}${hasQuery ? "&" : "?"}admin=true`;
  };

  // Responsive checks
  useEffect(() => {
    const checkIfMobile = () => setIsMobile(window.innerWidth <= 768);
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Close menus on route change / outside click
  useEffect(
    () => setIsMobileMenuOpen(false),
    [location.pathname, location.search]
  );
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMobileMenuOpen && !e.target.closest(".mobile-menu-container")) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobileMenuOpen]);

  // Update current URL for QR modal - use actual browser URL to capture all changes
  useEffect(() => {
    const updateUrl = () => {
      setCurrentUrl(window.location.href);
    };
    
    // Update immediately and on any URL change
    updateUrl();
    
    // Listen for both React Router changes and direct URL changes
    window.addEventListener('popstate', updateUrl);
    
    // Also check periodically for URL changes from pushState
    const interval = setInterval(updateUrl, 100);
    
    return () => {
      window.removeEventListener('popstate', updateUrl);
      clearInterval(interval);
    };
  }, []);

  const isActivePath = (path) => location.pathname === path;
  
  // Calculate total quantity including item quantities
  const totalCartItems = items.length + (catalogItems?.reduce((total, item) => {
    return total + (item.quantity || 1);
  }, 0) || 0);

  const handleRefresh = () => {
    clearCart();
    clearCatalogCart();
    window.location.href = "/";
  };

  const navLinks = [
    {
      path: "/waiver",
      icon: FileSignature,
      label: "Waiver",
      size: 28,
    },
    {
      path: "/packages",
      icon: Info,
      label: "Package Info",
      size: 28,
    },
    {
      path: "/image-gallery",
      icon: Image,
      label: "Photos & Videos",
      size: 28,
    },
    {
      path: "/items-catalog",
      icon: Crosshair,
      label: "Machine Guns",
      size: 28,
    },
    {
      path: "/vehicles",
      icon: Shield,
      label: "Vehicles",
      size: 28,
    },
    {
      path: "/dogtags",
      icon: Tag,
      label: "Dog Tags",
      size: 28,
    },
  ];

  const handleLogout = () => {
    localStorage.removeItem('uploadAuth');
    window.location.href = window.location.pathname;
  };

  const adminLinks = [
    {
      path: "/upload",
      icon: UploadIcon,
      label: "Upload",
      size: 28,
    },
    {
      path: "/admin",
      icon: Receipt,
      label: "Admin",
      size: 28,
    },
    {
      path: "/map",
      icon: Map,
      label: "Map",
      size: 28,
    },
    {
      action: handleLogout,
      icon: X,
      label: "Logout",
      size: 28,
    },
  ];

  return (
    <nav className="navigation">
      {isMobile ? (
        /* MOBILE NAVIGATION */
        <>
          <div className="mobile-menu-container">
            <button
              onClick={() => setIsMobileMenuOpen((o) => !o)}
              className={`mobile-menu-button ${isMobileMenuOpen ? 'active' : ''}`}
            >
              {isMobileMenuOpen ? <X size={32} /> : <Menu size={32} />}
            </button>

            {isMobileMenuOpen && (
              <div className="mobile-menu-overlay">
                <div className="mobile-menu-content">
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="mobile-menu-button active"
                    style={{ position: 'absolute', top: '20px', right: '20px' }}
                  >
                    <X size={32} />
                  </button>

                  <Link
                    to={makePath("/")}
                    className={`nav-link mobile ${isActivePath("/") ? 'active' : ''}`}
                    onClick={() => {
                      scrollToTop();
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <Home size={28} />
                    <span>Home</span>
                  </Link>

                  {navLinks.map(({ path, icon: Icon, label, size }) => (
                    <Link
                      key={path}
                      to={makePath(path)}
                      className={`nav-link mobile ${isActivePath(path) ? 'active' : ''}`}
                      onClick={() => {
                        scrollToTop();
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <Icon size={size} />
                      <span>{label}</span>
                    </Link>
                  ))}

                  {isAdmin && adminLinks.map(({ path, icon: Icon, label, size, action }) => (
                    path ? (
                      <Link
                        key={path}
                        to={makePath(path)}
                        className={`nav-link mobile ${isActivePath(path) ? 'active' : ''}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Icon size={size} />
                        <span>{label}</span>
                      </Link>
                    ) : (
                      <button
                        key={label}
                        onClick={() => {
                          action();
                          setIsMobileMenuOpen(false);
                        }}
                        className="nav-link mobile"
                      >
                        <Icon size={size} />
                        <span>{label}</span>
                      </button>
                    )
                  ))}

                  <Link
                    to={makePath("/cart")}
                    className={`nav-link mobile ${isActivePath("/cart") ? 'active' : ''}`}
                    onClick={() => {
                      scrollToTop();
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <ShoppingCart size={28} />
                    <span>Cart</span>
                    {totalCartItems > 0 && (
                      <span className="cart-badge mobile-menu">
                        {totalCartItems}
                      </span>
                    )}
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Centered logo */}
          <div className="mobile-logo">
            <Link to={makePath("/")} onClick={scrollToTop}>
              <img
                src="/images/datlogoA.png"
                alt="Drive A Tank"
                className="logo mobile"
              />
            </Link>
          </div>

          {/* Cart icon right */}
          <Link
            to={makePath("/cart")}
            className={`nav-link mobile-cart ${isActivePath("/cart") ? 'active' : ''}`}
          >
            <ShoppingCart size={28} />
            {totalCartItems > 0 && (
              <span className="cart-badge mobile-cart">
                {totalCartItems}
              </span>
            )}
          </Link>
        </>
      ) : (
        /* DESKTOP NAVIGATION */
        <>
          <Link to={makePath("/")} onClick={scrollToTop}>
            <img
              src="/images/datlogoA.png"
              alt="Drive A Tank"
              className="logo desktop"
            />
          </Link>

          <div className="nav-links">
            <button
              onClick={handleRefresh}
              className="nav-link refresh-button"
            >
              <RefreshCcw size={32} />
            </button>

            {navLinks.map(({ path, icon: Icon }) => (
              (isAdmin || !['/packages', '/image-gallery', '/items-catalog', '/vehicles', '/dogtags'].includes(path)) && (
                <Link
                  key={path}
                  to={makePath(path)}
                  className={`nav-link ${isActivePath(path) ? 'active' : ''}`}
                  onClick={scrollToTop}
                >
                  <Icon size={32} />
                </Link>
              )
            ))}

            {isAdmin && adminLinks.map(({ path, icon: Icon, action }) => (
              path ? (
                <Link
                  key={path}
                  to={makePath(path)}
                  className={`nav-link ${isActivePath(path) ? 'active' : ''}`}
                >
                  <Icon size={32} />
                </Link>
              ) : (
                <button
                  key="logout"
                  onClick={action}
                  className="nav-link"
                >
                  <Icon size={32} />
                </button>
              )
            ))}

            <Link
              to={makePath("/cart")}
              className={`nav-link cart-link ${isActivePath("/cart") ? 'active' : ''}`}
              onClick={scrollToTop}
            >
              <ShoppingCart size={32} />
              {totalCartItems > 0 && (
                <span className="cart-badge desktop">
                  {totalCartItems}
                </span>
              )}
            </Link>

            <button
              onClick={() => setShowQRModal(true)}
              className="nav-link qr-nav-button"
              title="Show QR Code"
            >
              <QrCode size={32} />
            </button>
          </div>

          <QRModal 
            isOpen={showQRModal} 
            onClose={() => setShowQRModal(false)} 
            url={currentUrl} 
          />
        </>
      )}
    </nav>
  );
}

export default Navigation;
